/**
 * Driver Schema
 * Zod validation schemas for driver forms
 */

import { z } from 'zod';

/**
 * CDL classes
 */
export const cdlClasses = ['A', 'B', 'C'] as const;

/**
 * Days of week
 */
export const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/**
 * Phone number regex (formats: (805) 555-1234, 805-555-1234, 8055551234)
 */
const phoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

/**
 * Create driver schema
 */
export const createDriverSchema = z.object({
  employeeNumber: z
    .string()
    .min(1, 'Employee number is required')
    .max(50, 'Employee number must be less than 50 characters')
    .regex(/^[A-Z0-9-]+$/i, 'Employee number must contain only letters, numbers, and hyphens'),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'First name must contain only letters, spaces, hyphens, and apostrophes'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Last name must contain only letters, spaces, hyphens, and apostrophes'),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Please enter a valid phone number (e.g., (805) 555-1234)'),
  email: z
    .string()
    .email('Please enter a valid email address')
    .max(100, 'Email must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  cdlNumber: z
    .string()
    .min(1, 'CDL number is required')
    .max(50, 'CDL number must be less than 50 characters')
    .regex(/^[A-Z0-9-]+$/i, 'CDL number must contain only letters, numbers, and hyphens'),
  cdlClass: z.enum(cdlClasses, {
    errorMap: () => ({ message: 'Please select a CDL class' }),
  }),
  cdlExpirationDate: z
    .string()
    .min(1, 'CDL expiration date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format')
    .refine((val) => {
      const expirationDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expirationDate >= today;
    }, 'CDL expiration date cannot be in the past'),
  endorsements: z.array(z.string()).optional().default([]),
  medicalCertExpiration: z
    .string()
    .refine((val) => val === '' || !isNaN(Date.parse(val)), 'Invalid date format')
    .refine((val) => {
      if (!val) return true;
      const expirationDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expirationDate >= today;
    }, 'Medical certification expiration date cannot be in the past')
    .optional()
    .or(z.literal('')),
  hireDate: z
    .string()
    .min(1, 'Hire date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
  availabilityDays: z
    .array(z.enum(daysOfWeek))
    .min(1, 'Select at least one available day')
    .default(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']),
  notes: z.string().max(500, 'Notes must be less than 500 characters').optional(),
});

/**
 * Assign route schema
 */
export const assignRouteSchema = z.object({
  routeId: z
    .string()
    .min(1, 'Route ID is required'),
  routeName: z
    .string()
    .min(1, 'Route name is required')
    .max(100, 'Route name must be less than 100 characters'),
  vehicleId: z
    .string()
    .min(1, 'Vehicle is required'),
});

/**
 * Add certification schema
 */
export const addCertificationSchema = z.object({
  certificationType: z
    .string()
    .min(1, 'Certification type is required')
    .max(100, 'Certification type must be less than 100 characters'),
  certificationNumber: z
    .string()
    .max(100, 'Certification number must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  issueDate: z
    .string()
    .min(1, 'Issue date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'),
  expirationDate: z
    .string()
    .refine((val) => val === '' || !isNaN(Date.parse(val)), 'Invalid date format')
    .optional()
    .or(z.literal('')),
}).refine(
  (data) => {
    if (!data.expirationDate) return true;
    const issueDate = new Date(data.issueDate);
    const expirationDate = new Date(data.expirationDate);
    return expirationDate > issueDate;
  },
  {
    message: 'Expiration date must be after issue date',
    path: ['expirationDate'],
  }
);

/**
 * Update availability schema
 */
export const updateAvailabilitySchema = z.object({
  daysAvailable: z
    .array(z.enum(daysOfWeek))
    .min(1, 'Select at least one available day'),
  timeOffDates: z
    .array(z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date format'))
    .optional()
    .default([]),
});

/**
 * Update CDL schema
 */
export const updateCDLSchema = z.object({
  cdlNumber: z
    .string()
    .min(1, 'CDL number is required')
    .max(50, 'CDL number must be less than 50 characters')
    .regex(/^[A-Z0-9-]+$/i, 'CDL number must contain only letters, numbers, and hyphens'),
  cdlClass: z.enum(cdlClasses, {
    errorMap: () => ({ message: 'Please select a CDL class' }),
  }),
  cdlExpirationDate: z
    .string()
    .min(1, 'CDL expiration date is required')
    .refine((val) => !isNaN(Date.parse(val)), 'Invalid date format')
    .refine((val) => {
      const expirationDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expirationDate >= today;
    }, 'CDL expiration date cannot be in the past'),
  endorsements: z.array(z.string()).optional().default([]),
  medicalCertExpiration: z
    .string()
    .refine((val) => val === '' || !isNaN(Date.parse(val)), 'Invalid date format')
    .refine((val) => {
      if (!val) return true;
      const expirationDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expirationDate >= today;
    }, 'Medical certification expiration date cannot be in the past')
    .optional()
    .or(z.literal('')),
});

/**
 * Type exports
 */
export type CreateDriverFormData = z.infer<typeof createDriverSchema>;
export type AssignRouteFormData = z.infer<typeof assignRouteSchema>;
export type AddCertificationFormData = z.infer<typeof addCertificationSchema>;
export type UpdateAvailabilityFormData = z.infer<typeof updateAvailabilitySchema>;
export type UpdateCDLFormData = z.infer<typeof updateCDLSchema>;
